// src/services/projetsServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/projets`;

/* Helpers */
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      const joined = v.map((x) => String(x).trim()).filter(Boolean).join(",");
      if (joined) usp.append(k, joined);
    } else if (typeof v === "boolean") {
      usp.append(k, v ? "true" : "false");
    } else {
      usp.append(k, String(v));
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/* ========= Public ========= */

// Tous les projets
export const getProjets = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllProjets`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des projets :", error);
    return [];
  }
};

// 3 derniers (Home)
export const getLastThreeProjets = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getLastThreeProjets`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des derniers projets :", error);
    return [];
  }
};

// Détail
export const getProjetById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement projet :", error);
    return null;
  }
};

/* ========= Admin ========= */

// Recherche / pagination
// params: { q, is_active, page=1, limit=24, sort='created_at'|'titre', dir='ASC'|'DESC' }
export const searchProjets = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche projets :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// Création
// payload: { titre*, description?, image_projet_url?, page_detail_url?, is_active? }
export const createProjet = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création projet :", error);
    return null;
  }
};

// Remplacement (PUT)
export const updateProjet = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour projet :", error);
    return null;
  }
};

// Patch partiel
export const patchProjet = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch projet :", error);
    return null;
  }
};

// Toggle actif
export const toggleProjetActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur toggle projet :", error);
    return null;
  }
};

// Suppression
export const deleteProjet = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression projet :", error);
    return false;
  }
};

// Helper optionnel pour l’URL d’image
export const toAbsoluteProjetImageUrl = (url) => {
  if (!url) return "";
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("uploads/")) {
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  }
  return url;
};
